import { createIntl, createIntlCache } from "react-intl";

import {
  isValidHttpUrlWithHostname,
  normalizePublicWebsiteUrl,
  normalizeWebsiteUrlWithDefaultHttps,
} from "@/lib/websiteUrl";
import { messages } from "@/routes/adManager/localization/AccountInfoFaviconUtils.messages";

import type { IntlShape } from "react-intl";

const DEFAULT_INTL = createIntl({ locale: "en" }, createIntlCache());

export function hasBusinessFavicon(faviconUrl: string | null | undefined): boolean {
  return faviconUrl != null && faviconUrl.trim().length > 0;
}

export const FAVICON_URL_LOOKUP_ERROR_MESSAGE =
  "We couldn't find a supported logo or favicon at that URL. Try a different website URL or upload a PNG, JPG, GIF, or WEBP logo.";

export const FAVICON_URL_INVALID_ERROR_MESSAGE = "Enter a valid image or website URL.";

export function isValidFaviconUrlInput(value: string): boolean {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return false;
  }

  try {
    const normalizedValue = normalizePublicWebsiteUrl(trimmedValue);
    return isValidHttpUrlWithHostname(normalizedValue);
  } catch {
    return false;
  }
}

export function getPrefillFaviconUrl(
  account:
    | {
        url?: string | null;
      }
    | null
    | undefined,
): string {
  return account?.url?.trim() ?? "";
}

export function getPreviewableFaviconUrlInput(value: string): string | null {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  try {
    const normalizedValue = normalizeWebsiteUrlWithDefaultHttps(trimmedValue);
    const url = new URL(normalizedValue);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    if (/\.(?:avif|gif|ico|jpe?g|png|svg|webp)$/i.test(url.pathname)) {
      return normalizedValue;
    }
    return new URL("/favicon.ico", url.origin).toString();
  } catch {
    return null;
  }
}

export function normalizeFaviconUrlInput(value: string, intl: IntlShape = DEFAULT_INTL): string {
  try {
    const normalizedUrl = normalizeWebsiteUrlWithDefaultHttps(value);
    const url = new URL(normalizedUrl);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      throw new Error(intl.formatMessage(messages.invalidUrl));
    }
    return normalizedUrl;
  } catch {
    throw new Error(intl.formatMessage(messages.invalidUrl));
  }
}

export function getFaviconUrlUploadErrorMessage(
  error: unknown,
  intl: IntlShape = DEFAULT_INTL,
): string | null {
  const details =
    (
      typeof error === "object" &&
      error != null &&
      "details" in error &&
      typeof error.details === "string"
    ) ?
      error.details
    : null;
  const message = error instanceof Error ? error.message : null;
  const errorText = [details, message].filter(Boolean).join("\n");

  if (errorText.includes("Could not find a supported logo or favicon")) {
    return intl.formatMessage(messages.lookupFailed);
  }
  if (
    errorText.includes("Logo URL must") ||
    errorText.includes("image_url must be an http(s) URL")
  ) {
    return intl.formatMessage(messages.invalidUrl);
  }
  return null;
}

export function hasSavedBusinessFavicon(account: {
  favicon_url?: string | null;
  preview_url?: string | null;
}): boolean {
  return hasBusinessFavicon(account.favicon_url) || hasBusinessFavicon(account.preview_url);
}

export const MIN_FAVICON_DIMENSION_PX = 128;

export async function validateFaviconFileDimensions(
  file: File,
  intl: IntlShape = DEFAULT_INTL,
): Promise<void> {
  const { width, height } = await readImageDimensions(file, intl);
  if (width < MIN_FAVICON_DIMENSION_PX || height < MIN_FAVICON_DIMENSION_PX) {
    throw new Error(
      intl.formatMessage(messages.dimensionsTooSmall, {
        minimumWidth: MIN_FAVICON_DIMENSION_PX,
        minimumHeight: MIN_FAVICON_DIMENSION_PX,
        width,
        height,
      }),
    );
  }
}

function readImageDimensions(
  file: File,
  intl: IntlShape,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new window.Image();

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };

    image.onload = () => {
      cleanup();
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
    image.onerror = () => {
      cleanup();
      reject(new Error(intl.formatMessage(messages.invalidImageFile)));
    };
    image.src = objectUrl;
  });
}
